import { useEffect, useRef, useState } from 'react';
import {
  BATCH_TRANSFER_MAX_APPLY_CARDS,
  type BatchImportMode,
  type BatchImportPreview,
  type BatchImportOutcome,
  type CombinedIdentificationPackage,
  type CombinedPackagePreview,
  type ChecklistCorrectionApplyRow,
} from '@tcvs/shared';
import type { ApiFailure } from '../auth/api-client';
import type { BatchTransferApi } from './batch-transfer-api';
import { CombinedPackageFileError, readCombinedPackageFiles } from './CombinedPackageFiles';

function reportId(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const id = (body as Record<string, unknown>).reportId;
  return typeof id === 'string' ? id : undefined;
}
function Values({ value }: { value: unknown }) {
  return (
    <pre className="tcvs-package-values">
      {value === undefined ? '—' : JSON.stringify(value, null, 2)}
    </pre>
  );
}
export function CombinedPackageTools({
  batchId,
  api,
  admin,
  disabled = false,
  resumeId,
  onReport,
  onApplying,
  onImported,
}: {
  batchId: string;
  api: BatchTransferApi;
  admin: boolean;
  disabled?: boolean;
  resumeId?: string;
  onReport: (id: string | null) => void;
  onApplying: (busy: boolean) => void;
  onImported: () => Promise<unknown> | void;
}) {
  const [saved, setSaved] = useState<CombinedIdentificationPackage | null>(null);
  const [preview, setPreview] = useState<CombinedPackagePreview | null>(null);
  const [identities, setIdentities] = useState<BatchImportPreview | null>(null);
  const [mode, setMode] = useState<BatchImportMode>('DRAFT');
  const [selectedCorrections, setSelectedCorrections] = useState(new Set<string>());
  const [selectedCards, setSelectedCards] = useState(new Set<string>());
  const [attested, setAttested] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [correctionOutcomes, setCorrectionOutcomes] = useState<ChecklistCorrectionApplyRow[]>([]);
  const [identityOutcomes, setIdentityOutcomes] = useState<BatchImportOutcome[]>([]);
  const generation = useRef(0);
  const currentBatch = useRef(batchId);
  currentBatch.current = batchId;
  const active = useRef<AbortController | null>(null);
  const lock = useRef(false);
  useEffect(() => {
    ++generation.current;
    setSaved(null);
    setPreview(null);
    setIdentities(null);
    setSelectedCards(new Set());
    setSelectedCorrections(new Set());
    setAttested(false);
    setError(null);
    setNotice(null);
    setBusy(null);
    setMode('DRAFT');
    setProgress(0);
    setCorrectionOutcomes([]);
    setIdentityOutcomes([]);
    lock.current = false;
    return () => {
      ++generation.current;
      active.current?.abort();
    };
  }, [batchId, resumeId]);
  const begin = (label: string, applying = false) => {
    if (lock.current) return null;
    lock.current = true;
    const version = ++generation.current;
    const batch = batchId;
    const controller = new AbortController();
    active.current = controller;
    setBusy(label);
    setError(null);
    setNotice(null);
    setProgress(0);
    if (applying) onApplying(true);
    return {
      signal: controller.signal,
      current: () => version === generation.current && batch === currentBatch.current,
      finish: () => {
        if (version === generation.current && batch === currentBatch.current) {
          lock.current = false;
          setBusy(null);
          if (applying) onApplying(false);
        }
      },
    };
  };
  const failed = (failure: ApiFailure) => {
    const id = reportId(failure.body);
    if (id) onReport(id);
    throw new Error(failure.message ?? failure.error);
  };
  const accept = (data: CombinedPackagePreview) => {
    if (data.package.batchId !== batchId || data.current.batchId !== batchId)
      throw new Error('The package response belongs to another batch.');
    setSaved(data.package);
    setMode(data.package.importMode);
    setPreview(data);
    setIdentities(null);
    setAttested(false);
    setSelectedCards(new Set());
    setSelectedCorrections(
      new Set(
        data.corrections?.rows
          .filter((row) => row.status === 'READY')
          .map((row) => row.correctionId) ?? [],
      ),
    );
    if (data.reportId) onReport(data.reportId);
  };
  useEffect(() => {
    if (!resumeId) return;
    const request = begin('Loading saved package');
    if (!request) return;
    void (async () => {
      try {
        const response = await api.getPackage(batchId, resumeId, request.signal);
        if (!request.current()) return;
        if (!response.ok) return failed(response);
        if (
          response.data.package.batchId !== batchId ||
          response.data.package.packageId !== resumeId
        )
          throw new Error('The saved package does not match this batch.');
        setSaved(response.data.package);
        setMode(response.data.package.importMode);
        setNotice(
          'Saved package reopened. Preview again to check current catalog and card records. Nothing has been applied.',
        );
        if (response.data.reportId) onReport(response.data.reportId);
      } catch (cause) {
        if (request.current())
          setError(cause instanceof Error ? cause.message : 'The package could not be reopened.');
      } finally {
        request.finish();
      }
    })();
    // Resume only when the batch/package changes; API callbacks do not trigger another read.
  }, [batchId, resumeId]);
  const upload = async (files: File[]) => {
    const request = begin('Reading and previewing package');
    if (!request) return;
    setSaved(null);
    setPreview(null);
    setIdentities(null);
    setSelectedCards(new Set());
    setSelectedCorrections(new Set());
    setAttested(false);
    setCorrectionOutcomes([]);
    setIdentityOutcomes([]);
    onReport(null);
    try {
      const data = await readCombinedPackageFiles(files, batchId);
      if (!request.current()) return;
      const response = await api.stagePackage(
        batchId,
        crypto.randomUUID(),
        data.results,
        data.corrections,
        mode,
        request.signal,
      );
      if (!request.current()) return;
      if (!response.ok) return failed(response);
      accept(response.data);
      setNotice('Package saved for review. Preview has not changed cards or the catalog.');
    } catch (cause) {
      if (!request.current()) return;
      setError(cause instanceof Error ? cause.message : 'The package could not be read.');
      if (cause instanceof CombinedPackageFileError) {
        try {
          const response = await api.logImportFailure(
            batchId,
            cause.reason,
            files
              .map((file) => file.name)
              .join(', ')
              .slice(0, 255),
            request.signal,
            mode,
          );
          if (!request.current()) return;
          if (response.ok) onReport(response.data.reportId);
          else setNotice('The package was rejected, but its diagnostic report could not be saved.');
        } catch {
          if (request.current())
            setNotice('The package was rejected, but its diagnostic report could not be saved.');
        }
      }
    } finally {
      request.finish();
    }
  };
  const refreshPackage = async () => {
    if (!saved) return;
    const request = begin('Checking current and proposed catalog');
    if (!request) return;
    setPreview(null);
    setIdentities(null);
    setAttested(false);
    setSelectedCards(new Set());
    try {
      const response = await api.previewPackage(batchId, saved.packageId, request.signal);
      if (!request.current()) return;
      if (!response.ok) return failed(response);
      accept(response.data);
    } catch (cause) {
      if (request.current()) setError(cause instanceof Error ? cause.message : 'Preview failed.');
    } finally {
      request.finish();
    }
  };
  const freshIdentities = async (
    request: NonNullable<ReturnType<typeof begin>>,
    packageId: string,
  ) => {
    const response = await api.previewPackageIdentities(batchId, packageId, request.signal);
    if (!request.current()) return;
    if (!response.ok) return failed(response);
    if (
      response.data.package.packageId !== packageId ||
      response.data.package.batchId !== batchId ||
      response.data.current.batchId !== batchId
    )
      throw new Error('The identity preview does not match this package.');
    setSaved(response.data.package);
    setIdentities(response.data.current);
    setAttested(false);
    setSelectedCards(new Set());
    if (response.data.reportId) onReport(response.data.reportId);
  };
  const refreshIdentities = async () => {
    if (!saved) return;
    const request = begin('Revalidating identities against the real catalog');
    if (!request) return;
    setIdentities(null);
    setAttested(false);
    setSelectedCards(new Set());
    try {
      await freshIdentities(request, saved.packageId);
    } catch (cause) {
      if (request.current())
        setError(cause instanceof Error ? cause.message : 'Identity preview failed.');
    } finally {
      request.finish();
    }
  };
  const applyCorrections = async () => {
    if (!admin || !saved || !preview?.corrections || !selectedCorrections.size || disabled) return;
    const request = begin('Applying selected catalog corrections', true);
    if (!request) return;
    const ids = [...selectedCorrections];
    const outcomes = new Map<string, ChecklistCorrectionApplyRow>();
    setIdentities(null);
    setAttested(false);
    setSelectedCards(new Set());
    try {
      for (let index = 0; index < ids.length; index += BATCH_TRANSFER_MAX_APPLY_CARDS) {
        if (!request.current()) return;
        const chunk = ids.slice(index, index + BATCH_TRANSFER_MAX_APPLY_CARDS);
        const response = await api.applyPackageCorrections(
          batchId,
          saved.packageId,
          chunk,
          preview.corrections.previewToken,
          request.signal,
        );
        if (!request.current()) return;
        if (!response.ok) return failed(response);
        if (response.data.reportId) onReport(response.data.reportId);
        for (const row of response.data.rows)
          if (chunk.includes(row.correctionId)) outcomes.set(row.correctionId, row);
        setCorrectionOutcomes([...outcomes.values()]);
        setProgress(Math.round((100 * outcomes.size) / ids.length));
        if (chunk.some((id) => !outcomes.has(id)))
          throw new Error(
            'Some correction outcomes are unconfirmed. Reopen and preview before retrying.',
          );
      }
      setNotice(
        'Catalog outcomes saved. Fresh identity preview uses the actual catalog, including any failed or pending corrections.',
      );
    } catch (cause) {
      if (request.current())
        setError(
          `${cause instanceof Error ? cause.message : 'Catalog application stopped.'} ${outcomes.size} outcomes confirmed; saved receipts remain in history. Reopen and preview before retrying.`,
        );
    } finally {
      if (request.current()) {
        setPreview(null);
        setSelectedCorrections(new Set());
        try {
          await freshIdentities(request, saved.packageId);
        } catch (cause) {
          if (request.current())
            setError(
              (old) =>
                `${old ? old + ' ' : ''}Fresh identity preview failed: ${cause instanceof Error ? cause.message : 'retry required'}`,
            );
        }
      }
      request.finish();
    }
  };
  const applyIdentities = async () => {
    if (
      !saved ||
      !identities ||
      !selectedCards.size ||
      disabled ||
      (mode === 'MANUAL_CONFIRMED' && !attested)
    )
      return;
    const request = begin('Applying selected identities', true);
    if (!request) return;
    const ids = [...selectedCards];
    const outcomes = new Map<string, BatchImportOutcome>();
    try {
      for (let index = 0; index < ids.length; index += BATCH_TRANSFER_MAX_APPLY_CARDS) {
        if (!request.current()) return;
        const chunk = ids.slice(index, index + BATCH_TRANSFER_MAX_APPLY_CARDS);
        const response = await api.applyPackageIdentities(
          batchId,
          saved.packageId,
          chunk,
          identities.previewToken,
          attested,
          request.signal,
        );
        if (!request.current()) return;
        if (!response.ok) return failed(response);
        if (response.data.reportId) onReport(response.data.reportId);
        for (const row of response.data.rows)
          if (chunk.includes(row.cardId)) outcomes.set(row.cardId, row);
        setIdentityOutcomes([...outcomes.values()]);
        setProgress(Math.round((100 * outcomes.size) / ids.length));
        if (chunk.some((id) => !outcomes.has(id)))
          throw new Error(
            'Some identity outcomes are unconfirmed. Reopen and preview before retrying.',
          );
      }
      setNotice('Selected identity outcomes saved. Review each result below.');
    } catch (cause) {
      if (request.current())
        setError(
          `${cause instanceof Error ? cause.message : 'Identity application stopped.'} ${outcomes.size} outcomes confirmed; saved receipts remain in history.`,
        );
    } finally {
      if (request.current()) {
        setIdentities(null);
        setSelectedCards(new Set());
        setAttested(false);
        if ([...outcomes.values()].some((row) => row.status === 'APPLIED')) {
          try {
            await onImported();
          } catch {
            if (request.current())
              setNotice('Some identities were applied. Reload the batch to see them.');
          }
        }
      }
      request.finish();
    }
  };
  return (
    <section aria-label="Combined review package">
      <p>
        Upload identification-results.json and optional checklist-corrections.json together, or a
        ZIP containing those files at its root. Maximum 2 MiB total; corrections maximum 512 KiB.
        Product files are not supported.
      </p>
      <fieldset disabled={busy !== null || !!saved || disabled} className="tcvs-transfer-mode">
        <legend>Package identification mode</legend>
        <label>
          <input
            type="radio"
            name="package-mode"
            checked={mode === 'DRAFT'}
            onChange={() => setMode('DRAFT')}
          />
          Review drafts
        </label>
        <label>
          <input
            type="radio"
            name="package-mode"
            checked={mode === 'MANUAL_CONFIRMED'}
            onChange={() => setMode('MANUAL_CONFIRMED')}
          />
          Manually confirmed identifications
        </label>
      </fieldset>
      <label>
        Review package ZIP or JSON files{' '}
        <input
          type="file"
          multiple
          accept=".zip,.json,application/zip,application/json"
          disabled={busy !== null || disabled}
          onChange={(event) => {
            const files = Array.from(event.currentTarget.files ?? []);
            event.currentTarget.value = '';
            if (files.length) void upload(files);
          }}
        />
      </label>
      <div className="tcvs-package-feedback" aria-live="polite">
        {error ? (
          <p role="alert" className="tcvs-error">
            {error}
          </p>
        ) : null}
        {notice ? <p role="status">{notice}</p> : null}
        {busy ? <p role="status">{busy}…</p> : null}
      </div>
      <div className="tcvs-export-progress">
        <div
          className="tcvs-export-progress-track"
          role="progressbar"
          aria-label="Review package progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <span className="tcvs-export-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="tcvs-export-progress-percent">{progress}%</span>
      </div>
      {saved ? (
        <>
          <p>
            Saved package {saved.packageId} · {saved.results.runId} ·{' '}
            {mode === 'MANUAL_CONFIRMED' ? 'Manual confirmation' : 'Drafts'}
          </p>
          <p>
            You may close this window and resume from Import history. Reopening never applies
            changes automatically.
          </p>
          {saved.reportIds?.length ? (
            <details>
              <summary>Saved package reports</summary>
              <ul>
                {saved.reportIds.map((id) => (
                  <li key={id}>
                    <button type="button" disabled={busy !== null} onClick={() => onReport(id)}>
                      Open report {id}
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          <div className="tcvs-transfer-actions">
            <button
              type="button"
              disabled={busy !== null || disabled}
              onClick={() => void refreshPackage()}
            >
              Preview package again
            </button>
            <button
              type="button"
              disabled={busy !== null || disabled}
              onClick={() => void refreshIdentities()}
            >
              Revalidate identities against real catalog
            </button>
          </div>
        </>
      ) : null}
      {preview?.corrections ? (
        <>
          <h4>1. Catalog corrections</h4>
          <div className="tcvs-transfer-table">
            <table>
              <thead>
                <tr>
                  <th>Apply</th>
                  <th>Correction / reason</th>
                  <th>Current catalog</th>
                  <th>Proposed catalog</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {preview.corrections.rows.map((row) => (
                  <tr key={row.correctionId}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Package correction ${row.correctionId}`}
                        checked={selectedCorrections.has(row.correctionId)}
                        disabled={!admin || disabled || busy !== null || row.status !== 'READY'}
                        onChange={(event) => {
                          const checked = event.currentTarget.checked;
                          setSelectedCorrections((old) => {
                            const next = new Set(old);
                            if (checked) next.add(row.correctionId);
                            else next.delete(row.correctionId);
                            return next;
                          });
                        }}
                      />
                    </td>
                    <td>
                      {row.correctionId}
                      <p>
                        {
                          saved?.corrections?.changes.find(
                            (change) => change.correctionId === row.correctionId,
                          )?.reason
                        }
                      </p>
                    </td>
                    <td>
                      <Values value={row.current} />
                    </td>
                    <td>
                      <Values value={row.proposed} />
                    </td>
                    <td>
                      {row.status}
                      <p>{row.message}</p>
                      <p>{row.resolution}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {admin ? (
            <button
              type="button"
              disabled={busy !== null || disabled || !selectedCorrections.size}
              onClick={() => void applyCorrections()}
            >
              Apply selected catalog corrections ({selectedCorrections.size})
            </button>
          ) : (
            <p>
              An administrator must approve and apply catalog corrections. This saved package is
              available to another administrator through Import history.
            </p>
          )}
        </>
      ) : null}
      {preview ? (
        <>
          <h4>Current versus hypothetical identity matches</h4>
          <p>
            Hypothetical results assume eligible catalog corrections succeed. They are advisory and
            cannot be applied. Use a fresh real-catalog preview before applying identities.
          </p>
          <div className="tcvs-transfer-table">
            <table>
              <thead>
                <tr>
                  <th>Card</th>
                  <th>Current catalog match</th>
                  <th>If eligible corrections succeed</th>
                  <th>Source dependencies</th>
                </tr>
              </thead>
              <tbody>
                {preview.current.rows.map((row) => (
                  <tr key={row.cardId}>
                    <td>{row.shortCode ?? row.cardId}</td>
                    <td>
                      {row.status}
                      <p>{row.reason}</p>
                      {row.checklistSourceSelection ? (
                        <p>Exact current checklist source agrees.</p>
                      ) : (
                        <p>No exact current checklist source established.</p>
                      )}
                      {row.catalogWarnings?.map((warning, index) => (
                        <p key={index}>{warning.message}</p>
                      ))}
                    </td>
                    <td>
                      {preview.hypothetical?.rows.find(
                        (candidate) => candidate.cardId === row.cardId,
                      )?.status ?? 'No proposed catalog change'}
                      <p>
                        {
                          preview.hypothetical?.rows.find(
                            (candidate) => candidate.cardId === row.cardId,
                          )?.reason
                        }
                      </p>
                      {preview.hypothetical?.rows
                        .find((candidate) => candidate.cardId === row.cardId)
                        ?.catalogWarnings?.map((warning, index) => (
                          <p key={index}>
                            {warning.message} {warning.resolution}
                          </p>
                        ))}
                      {preview.hypothetical?.rows.find(
                        (candidate) => candidate.cardId === row.cardId,
                      )?.checklistSourceSelection ? (
                        <p>Exact checklist source agrees after eligible corrections.</p>
                      ) : preview.hypothetical ? (
                        <p>No exact checklist source established.</p>
                      ) : null}
                    </td>
                    <td>
                      {preview.dependencies
                        .find((item) => item.cardId === row.cardId)
                        ?.correctionIds.join(', ') || 'None'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
      {identities ? (
        <>
          <h4>2. Fresh identity preview against the real catalog</h4>
          <p>
            Failed or pending catalog corrections remain warnings. Manually checked identities can
            still be accepted when eligible; card, image and listing protections remain in force.
          </p>
          <div className="tcvs-transfer-actions">
            <button
              type="button"
              disabled={busy !== null || disabled}
              onClick={() => {
                setSelectedCards(
                  new Set(
                    identities.rows
                      .filter((row) => row.status === 'READY')
                      .map((row) => row.cardId),
                  ),
                );
                setAttested(false);
              }}
            >
              Select eligible identities
            </button>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => {
                setSelectedCards(new Set());
                setAttested(false);
              }}
            >
              Clear identities
            </button>
          </div>
          <div className="tcvs-transfer-table">
            <table>
              <thead>
                <tr>
                  <th>Apply</th>
                  <th>Card</th>
                  <th>Current identity</th>
                  <th>Proposed identity</th>
                  <th>Result / checklist differences</th>
                </tr>
              </thead>
              <tbody>
                {identities.rows.map((row) => (
                  <tr key={row.cardId}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Package card ${row.cardId}`}
                        checked={selectedCards.has(row.cardId)}
                        disabled={busy !== null || disabled || row.status !== 'READY'}
                        onChange={(event) => {
                          const checked = event.currentTarget.checked;
                          setSelectedCards((old) => {
                            const next = new Set(old);
                            if (checked) next.add(row.cardId);
                            else next.delete(row.cardId);
                            return next;
                          });
                          setAttested(false);
                        }}
                      />
                    </td>
                    <td>{row.shortCode ?? row.cardId}</td>
                    <td>
                      <Values value={row.current} />
                    </td>
                    <td>
                      <Values value={row.proposed} />
                    </td>
                    <td>
                      {row.status}
                      <p>{row.reason}</p>
                      <p>{row.resolution}</p>
                      {row.catalogWarnings?.map((warning, index) => (
                        <p key={index}>
                          {warning.message} {warning.resolution}
                        </p>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {mode === 'MANUAL_CONFIRMED' ? (
            <label className="tcvs-transfer-attestation">
              <input
                type="checkbox"
                checked={attested}
                disabled={busy !== null || disabled || !selectedCards.size}
                onChange={(event) => setAttested(event.currentTarget.checked)}
              />
              I have manually checked the selected cards against their images and confirm these
              identities. Producer labels and model confidence do not supply this attestation.
            </label>
          ) : null}
          <button
            type="button"
            disabled={
              busy !== null ||
              disabled ||
              !selectedCards.size ||
              (mode === 'MANUAL_CONFIRMED' && !attested)
            }
            onClick={() => void applyIdentities()}
          >
            {mode === 'MANUAL_CONFIRMED' ? 'Confirm selected identities' : 'Save selected drafts'} (
            {selectedCards.size})
          </button>
        </>
      ) : null}
      {correctionOutcomes.length ? (
        <>
          <h4>Saved catalog outcomes</h4>
          <ul>
            {correctionOutcomes.map((row) => (
              <li key={row.correctionId}>
                {row.correctionId} · {row.status}
                <p>
                  {row.message} {row.resolution}
                </p>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {identityOutcomes.length ? (
        <>
          <h4>Saved identity outcomes</h4>
          <ul>
            {identityOutcomes.map((row) => (
              <li key={row.cardId}>
                {row.cardId} · {row.status}
                <p>
                  {row.reason} {row.resolution}
                </p>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
